'use client';

import {
  Flex,
  freddyTheme,
  Grid,
  GridItem,
  SelectableBoxGroup,
  SelectableCheckBox
} from '@coolblue-development/freddy';
import { useTranslation } from '@i18n/useTranslation';
import React from 'react';

import { MatrixValuesType } from './MatrixHelper';

type Props = {
  matrixValues: MatrixValuesType;
  setMatrixValues: React.Dispatch<React.SetStateAction<MatrixValuesType>>;
  columnLabels: string[];
  rowLabels: string[];
};

export const MatrixCards = ({ matrixValues, setMatrixValues, columnLabels, rowLabels }: Props) => {
  const { t } = useTranslation('vacancyDetail.applicationForm.questions.matrixQuestion');

  return (
    <Flex direction='column' gap={200}>
      {columnLabels.map(label => (
        <SelectableBoxGroup
          key={label}
          legend={label}
          showLegend={false}
          controlPlacement='hidden'
          density='compact'
          value={Array.from(matrixValues.get(label) || [])
            .map(([key, value]) => (value ? `${label} - ${key}` : undefined))
            .filter(v => v !== undefined)}
          onChange={(values: string[]) => {
            const rowLabelsSelected = new Set(values.map(value => value.split(' - ')[1]));
            const updatedMatrix = new Map(matrixValues);
            for (const rowLabel of rowLabels) {
              const isSelected = rowLabelsSelected.has(rowLabel);
              updatedMatrix.get(label)?.set(rowLabel, isSelected);
            }
            setMatrixValues(updatedMatrix);
          }}
        >
          <Grid
            templateColumns={`${freddyTheme.spacing[700]} repeat(${rowLabels.length}, 1fr)`}
            gap={700}
            alignItems='center'
            sx={{ width: '100%' }}
          >
            <GridItem column='1'>{label}</GridItem>
            {rowLabels.map(rowLabel => {
              const isChecked = matrixValues.get(label)?.get(rowLabel) || false;

              return (
                <SelectableCheckBox
                  key={`${label}-${rowLabel}`}
                  value={`${label} - ${rowLabel}`}
                  ariaLabel={
                    isChecked
                      ? t('deselectOption', { rowLabel, columnLabel: label })
                      : t('selectOption', { rowLabel, columnLabel: label })
                  }
                  sx={{ width: '100%', justifyContent: 'center' }}
                >
                  {rowLabel}
                </SelectableCheckBox>
              );
            })}
          </Grid>
        </SelectableBoxGroup>
      ))}
    </Flex>
  );
};
